import { createStandaloneToast } from '@chakra-ui/react';
import {
  getDistanceToSegment,
  projectPointOnSegment,
  getSegmentIntersection,
  isPoint2,
  pointsAreEqual,
  areSegmentsEqual,
  calculateRelativePosition,
} from 'src/components/orderContractor/shapes/util';
import {
  SheetShapeDeepFragment,
  GetSparkelPropertiesForProjectQuery,
  SheetScaleType,
} from 'src/gql/graphql';
import { Point2, LineSegment2 } from 'src/domain/geometry/geometric-types';
import { toPoint2 } from 'src/domain/geometry/algorithms/util/polygon';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { ChakraColor } from 'src/utils/color-util';
import {
  computeTrapezoids,
  trapezoidToPolygon2,
  Trapezoid2,
  computePlanSlices,
} from 'src/components/orderContractor/pdf-shapes/protan/useTaperedInsulation';
import { SlopedInsulationCrossSection } from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';
import {
  ABSOLUTE_PROXIMITY_THRESHOLD,
  getCrossSectionFromProperties,
  getRoofShapeFromProperties,
} from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import { Substrate } from 'src/components/orderContractor/ProductChoices';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import Edit2D = Autodesk.Extensions.Edit2D;
import { calculateRoofUValue } from 'src/components/orderContractor/pdf-shapes/protan/thermal-calculation-util';
import { getTaperedInsulationBuildup } from 'src/components/orderContractor/pdf-shapes/protan/tapered-insulation-products';

// Constants for cross section computation
const VERTICAL_MULTIPLIER = 5;
const SLOPE = 0.025; // 1:40;
const OFFSET_FROM_ROOF = 50;

interface DrainLineState {
  drainLines: LineSegment2[];
  baseLine: LineSegment2 | null;
  activeShape: SheetShapeDeepFragment | null;
  intermediateEdge: LineSegment2 | null;
  crossSection: SlopedInsulationCrossSection | null;
}

export class DrainLineDrawingTool extends Autodesk.Viewing.ToolInterface {
  names = [ActiveTool.DrainLine];

  private _state: DrainLineState = {
    drainLines: [],
    baseLine: null,
    activeShape: null,
    intermediateEdge: null,
    crossSection: null,
  };
  private _previewShape: Autodesk.Edit2D.Polyline | null = null;
  private _staticShapes: Autodesk.Edit2D.Polyline[] = [];
  private _crossSectionPreviewShapes: Autodesk.Edit2D.Shape[] = [];
  private _actualCrossSectionShapes: Autodesk.Edit2D.Shape[] = [];

  private _shapes: SheetShapeDeepFragment[] = [];
  private _onResult?: (result: any) => void;
  private _propertyData?: GetSparkelPropertiesForProjectQuery;
  private _shapeToEdit?: SheetShapeDeepFragment | null;
  private _minHeight = 0.26;
  private _calibration?: SheetScaleType | null;

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly activeSheetData: ActiveSheetData,
    private readonly edit2d: Edit2D,
    shapes: SheetShapeDeepFragment[],
    onResult?: (result: any) => void,
    propertyData?: GetSparkelPropertiesForProjectQuery,
    shapeToEdit?: SheetShapeDeepFragment | null,
    minHeight = 0.26,
    calibration?: SheetScaleType | null
  ) {
    super();
    this._shapes = shapes;
    this._onResult = onResult;
    this._propertyData = propertyData;
    this._shapeToEdit = shapeToEdit;
    this._minHeight = minHeight;
    this._calibration = calibration;

    // Initialize state from shape to edit if provided
    if (shapeToEdit) {
      this._initializeFromShape(shapeToEdit);
    }

    // Remove default methods to prevent conflicts
    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).handleButtonDown;
    delete (this as any).handleButtonUp;
    delete (this as any).getPriority;
    delete (this as any).activate;
    delete (this as any).deactivate;
  }

  private _initializeFromShape(shape: SheetShapeDeepFragment): void {
    if (!shape.sheetShapeSplitLine) return;

    // Convert points from the shape to LineSegment2 format
    const points = shape.sheetShapeSplitLine.points;
    const drainLines: LineSegment2[] = [];

    for (let i = 0; i < points.length - 1; i += 2) {
      if (i + 1 < points.length) {
        drainLines.push([
          [points[i].x, points[i].y],
          [points[i + 1].x, points[i + 1].y],
        ]);
      }
    }

    // Get cross section from properties
    const crossSection = getCrossSectionFromProperties(
      this._propertyData,
      shape.dbId,
      shape.urn
    );

    // Use cross section as-is (already in meters)
    const crossSectionForTool = crossSection;

    // Update the minHeight with the actual value from the cross section
    if (crossSectionForTool?.minHeight) {
      this._minHeight = crossSectionForTool.minHeight;
    }

    // Find the active shape (the shape that contains the drain lines)
    const activeShape = this._findActiveShape(drainLines[0]?.[0]);

    // Set up initial state
    this._state = {
      drainLines,
      baseLine: crossSectionForTool?.baseLine || null,
      activeShape,
      intermediateEdge: null,
      crossSection: crossSectionForTool || null,
    };

    // Render the initial state
    this._renderStaticShapes();
    if (crossSection) {
      this._renderActualCrossSection();
    }
  }

  private _findActiveShape(point: Point2): SheetShapeDeepFragment | null {
    if (!point) return null;

    for (const shape of this._shapes) {
      const polygons = shape.sheetShapePolygon?.multipolygon.polygons;
      if (!polygons) continue;

      for (const polygon of polygons) {
        const edges = polygon.exterior.points.map(
          (point, index) =>
            [
              toPoint2(point),
              toPoint2(
                polygon.exterior.points[
                  (index + 1) % polygon.exterior.points.length
                ]
              ),
            ] as LineSegment2
        );

        for (const edge of edges) {
          const distance = getDistanceToSegment(point, edge) as number;
          if (distance < 0.01) {
            return shape;
          }
        }
      }
    }

    return null;
  }

  getPriority(): number {
    return 100;
  }

  activate(): void {
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.MIXED);
    // Set initial cursor
    this.viewer.impl.canvas.style.cursor = 'crosshair';
  }

  deactivate(): void {
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.REGULAR);
    this.clear();
    // Reset cursor to default
    this.viewer.impl.canvas.style.cursor = 'default';
  }

  clear(): void {
    this._clearIntermediateEdge();
    this._clearStaticShapes();
    this._clearCrossSectionPreview();
    this._clearActualCrossSection();

    // Reset state to initial values
    this._state = {
      drainLines: [],
      baseLine: null,
      activeShape: null,
      intermediateEdge: null,
      crossSection: null,
    };
  }

  setMinHeight(minHeight: number): void {
    this._minHeight = minHeight;

    // Also update the stored cross section if we're editing an existing shape
    if (this._state.crossSection) {
      this._state.crossSection = {
        ...this._state.crossSection,
        minHeight: minHeight,
      };
    }

    // Update cross section if we have drain lines
    if (this._state.drainLines.length > 0) {
      this._updateShapes();
    }
  }

  getMinHeight(): number {
    return this._minHeight;
  }

  // Public method to get drain line results for the hook
  getResults(): {
    valid: boolean;
    crossSection: SlopedInsulationCrossSection | undefined;
    roofShapeDbId: number | undefined;
    drainLines: LineSegment2[];
    productAreas: Record<string, number>;
    roofUValue: number | null;
    totalVolume: number;
  } {
    const crossSection = this._computeCrossSection();

    // Compute product areas if we have a valid cross section
    let productAreas: Record<string, number> = {};
    let totalVolume = 0;
    let roofUValue: number | null = null;

    if (crossSection && this._state.activeShape) {
      try {
        // Get calibrated unit scale from the stored calibration
        const unitScale = this._calibration
          ? getUnitScaleFromCalibration(this._calibration)
          : 1;

        // Resolve roofShapeDbId (prefer explicit property on the drain line shape if available)
        const resolvedRoofShapeDbId = (() => {
          if (this._shapeToEdit && this._propertyData) {
            const maybeRoofShapeDbId = getRoofShapeFromProperties(
              this._propertyData,
              this._shapeToEdit.dbId,
              this._shapeToEdit.urn
            );
            if (maybeRoofShapeDbId) return maybeRoofShapeDbId;
          }
          return this._state.activeShape?.dbId;
        })();

        // Get substrate from sparkel properties for the roof shape (default to Steel)
        const substrate = (() => {
          const urn = this._state.activeShape?.urn;
          const propertyValue =
            this._propertyData?.project?.sparkelProperties.find(
              (prop) =>
                prop.propertySet === 'Substrate' &&
                prop.dbId === resolvedRoofShapeDbId &&
                (urn ? prop.modelUrn === urn : true)
            )?.propertyValue;

          const validValues = Object.values(Substrate) as string[];
          return validValues.includes(propertyValue || '')
            ? (propertyValue as Substrate)
            : Substrate.Steel;
        })();

        // If roof shape has Fire sectioning set (non-empty and not "0"), skip plan slices
        const hasFireSectioning = (() => {
          const urn = this._state.activeShape?.urn;
          const propertyValue =
            this._propertyData?.project?.sparkelProperties.find(
              (prop) =>
                prop.propertySet === 'Fire sectioning' &&
                prop.dbId === resolvedRoofShapeDbId &&
                (urn ? prop.modelUrn === urn : true)
            )?.propertyValue;

          if (propertyValue === null || propertyValue === undefined)
            return false;
          const str = String(propertyValue).trim();
          if (str.length === 0) return false;
          if (str === '0') return false;
          return true;
        })();

        // Get rendered sheet shapes for plan slice computation
        const renderedSheetShapes = this._shapes.reduce((acc, shape) => {
          const urn = shape.urn;
          if (!acc[urn]) {
            acc[urn] = [];
          }
          acc[urn].push(shape);
          return acc;
        }, {} as Record<string, any[]>);

        // Compute plan slices to get product areas unless Fire sectioning is set
        const planSlices = hasFireSectioning
          ? []
          : computePlanSlices({
              crossSection,
              substrate,
              unitScale,
              renderedSheetShapes,
              roofShapeDbId: resolvedRoofShapeDbId,
            });

        // Aggregate product areas from plan slices
        // Use boundingBoxArea like the useInsulationProductSlices hook does
        planSlices.forEach((slice) => {
          slice.products.forEach((product) => {
            const area = slice.boundingBoxArea || 0;
            productAreas[product.sku] = (productAreas[product.sku] || 0) + area;
          });
        });

        totalVolume = planSlices.reduce((acc, slice) => {
          return acc + slice.volume;
        }, 0);

        roofUValue =
          planSlices.length === 0
            ? null
            : calculateRoofUValue(
                getTaperedInsulationBuildup(substrate),
                planSlices
              );
      } catch (error) {
        console.warn('Error computing product areas:', error);
      }
    }

    return {
      valid: this._state.drainLines.length > 0,
      crossSection: crossSection || undefined,
      roofShapeDbId: this._state.activeShape?.dbId,
      drainLines: this._state.drainLines,
      productAreas,
      roofUValue,
      totalVolume,
    };
  }

  getCursor(): string {
    return 'crosshair';
  }

  handleSingleClick(event: MouseEvent, button: number): boolean {
    if (button !== 0) return false;

    // Get the viewer element to calculate header offset
    const viewerElement = this.viewer.impl.canvas.parentElement;
    if (!viewerElement) return false;

    const viewerRect = viewerElement.getBoundingClientRect();

    // Calculate the point relative to the viewer (accounting for header offset)
    const relativeX = event.clientX - viewerRect.left;
    const relativeY = event.clientY - viewerRect.top;

    const cursorPosition = this.viewer.impl.intersectGround(
      relativeX,
      relativeY
    );

    if (!cursorPosition) return false;

    // First, check if we clicked on an existing static drain line
    if (this._handleStaticLineClick(cursorPosition)) {
      return true;
    }

    // Convert to API coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );
    const apiPoint = converter.convertToApiPoint(cursorPosition);

    this._handleClick([apiPoint.x, apiPoint.y]);
    return true;
  }

  handleMouseMove(event: MouseEvent): boolean {
    // Get the viewer element to calculate header offset
    const viewerElement = this.viewer.impl.canvas.parentElement;
    if (!viewerElement) return false;

    const viewerRect = viewerElement.getBoundingClientRect();

    // Calculate the point relative to the viewer (accounting for header offset)
    const relativeX = event.clientX - viewerRect.left;
    const relativeY = event.clientY - viewerRect.top;

    const cursorPosition = this.viewer.impl.intersectGround(
      relativeX,
      relativeY
    );

    if (!cursorPosition) return false;

    // Check for hover on static lines to update cursor
    this._handleStaticLineHover(cursorPosition);

    // Convert to API coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );
    const apiPoint = converter.convertToApiPoint(cursorPosition);

    this._handleMouseMove([apiPoint.x, apiPoint.y]);
    return false;
  }

  private _handleClick(point: Point2): void {
    // If we already have drain lines, create parallel split segment
    if (
      this._state.baseLine &&
      this._state.activeShape &&
      this._state.drainLines.length > 0
    ) {
      const drainLinesDirection: Point2 = [
        this._state.drainLines[0][0][0] - this._state.drainLines[0][1][0],
        this._state.drainLines[0][0][1] - this._state.drainLines[0][1][1],
      ];

      const splitSegment = this._getDirectedSegmentFromPointInShape(
        point,
        drainLinesDirection,
        this._state.activeShape
      );

      if (splitSegment) {
        const snappedSplitSegment = this._createSnappedSegment(
          splitSegment,
          this._state.baseLine
        );
        this._state.drainLines.push(snappedSplitSegment);
        this._updateShapes();
      }
    } else {
      // First drain line - clear intermediate preview
      this._clearCrossSectionPreview();

      const { segment: splitSegment, shape } =
        this._getSplitSegmentFromPoint(point);

      // Prevent placement on shapes with Fire sectioning
      if (splitSegment && shape) {
        const hasFireSectioning = (() => {
          const urn = shape.urn;
          const propertyValue =
            this._propertyData?.project?.sparkelProperties.find(
              (prop) =>
                prop.propertySet === 'Fire sectioning' &&
                prop.dbId === shape.dbId &&
                (urn ? prop.modelUrn === urn : true)
            )?.propertyValue;
          if (propertyValue === null || propertyValue === undefined)
            return false;
          const str = String(propertyValue).trim();
          if (str.length === 0) return false;
          if (str === '0') return false;
          return true;
        })();
        if (hasFireSectioning) {
          const { toast } = createStandaloneToast();
          toast({
            status: 'error',
            duration: 3000,
            position: 'top',
            title: 'Tapered insulation not supported for combustible roofs',
          });
          return; // clicking does nothing
        }

        const newBaseLine = this._getBaseLineFromSplitSegment(
          splitSegment,
          shape,
          point
        );

        if (newBaseLine) {
          const snappedSplitSegment = this._createSnappedSegment(
            splitSegment,
            newBaseLine
          );
          this._state.baseLine = newBaseLine;
          this._state.activeShape = shape;
          this._state.drainLines = [snappedSplitSegment];
          this._updateShapes();
        }
      }
    }
  }

  private _handleMouseMove(point: Point2): void {
    // If we already have a crossSection, create parallel split segment
    if (
      this._state.baseLine &&
      this._state.activeShape &&
      this._state.drainLines.length > 0
    ) {
      this._clearIntermediateEdge();
      this._clearCrossSectionPreview();

      const drainLinesDirection: Point2 = [
        this._state.drainLines[0][1][0] - this._state.drainLines[0][0][0],
        this._state.drainLines[0][1][1] - this._state.drainLines[0][0][1],
      ];

      const splitSegment = this._getDirectedSegmentFromPointInShape(
        point,
        drainLinesDirection,
        this._state.activeShape
      );

      if (splitSegment) {
        const snappedSplitSegment = this._createSnappedSegment(
          splitSegment,
          this._state.baseLine
        );
        this._state.intermediateEdge = snappedSplitSegment;
        this._drawIntermediateEdge();
      }
    } else {
      // Original logic for first movement - show preview of cross section
      this._clearIntermediateEdge();
      this._clearCrossSectionPreview();

      const { segment: splitSegment, shape } =
        this._getSplitSegmentFromPoint(point);

      if (splitSegment && shape) {
        // Skip preview if Fire sectioning is set on the shape under cursor
        const hasFireSectioning = (() => {
          const urn = shape.urn;
          const propertyValue =
            this._propertyData?.project?.sparkelProperties.find(
              (prop) =>
                prop.propertySet === 'Fire sectioning' &&
                prop.dbId === shape.dbId &&
                (urn ? prop.modelUrn === urn : true)
            )?.propertyValue;

          if (propertyValue === null || propertyValue === undefined)
            return false;
          const str = String(propertyValue).trim();
          if (str.length === 0) return false;
          if (str === '0') return false;
          return true;
        })();
        if (hasFireSectioning) {
          this._state.intermediateEdge = splitSegment;
          this._drawIntermediateEdge(ChakraColor.red400);
          this._clearCrossSectionPreview();
          return;
        }

        this._state.intermediateEdge = splitSegment;
        this._drawIntermediateEdge();

        const newBaseLine = this._getBaseLineFromSplitSegment(
          splitSegment,
          shape,
          point
        );

        if (newBaseLine) {
          const snappedSplitSegment = this._createSnappedSegment(
            splitSegment,
            newBaseLine
          );
          this._state.intermediateEdge = snappedSplitSegment;
          this._drawIntermediateEdge();

          // Calculate intermediate cross section preview
          this._renderIntermediateCrossSectionPreview(
            point,
            newBaseLine,
            snappedSplitSegment
          );
        } else {
          this._clearCrossSectionPreview();
        }
      } else {
        this._clearCrossSectionPreview();
      }
    }
  }

  // eslint-disable-next-line complexity
  private _getSplitSegmentFromPoint(point: Point2): {
    segment: LineSegment2 | null;
    shape: SheetShapeDeepFragment | null;
  } {
    const candidates: [LineSegment2, SheetShapeDeepFragment][] = [];

    for (const shape of this._shapes) {
      const polygons = shape.sheetShapePolygon?.multipolygon.polygons;
      if (!polygons) continue;

      for (const polygon of polygons) {
        const edges = polygon.exterior.points.map(
          (point, index) =>
            [
              toPoint2(point),
              toPoint2(
                polygon.exterior.points[
                  (index + 1) % polygon.exterior.points.length
                ]
              ),
            ] as LineSegment2
        );

        const closestEdgeForThisPolygon = this._getClosestEdge(point, edges);

        if (closestEdgeForThisPolygon) {
          candidates.push([closestEdgeForThisPolygon, shape]);
          break;
        }
      }
    }

    if (candidates.length === 0) {
      return { segment: null, shape: null };
    }

    const [closestEdge, closestShape] = candidates.reduce((prev, curr) =>
      getDistanceToSegment(point, curr[0]) <
      getDistanceToSegment(point, prev[0])
        ? curr
        : prev
    );

    if (closestEdge && closestShape) {
      const projectedPoint = projectPointOnSegment(point, closestEdge);

      const directionVector: Point2 = [
        point[0] - projectedPoint[0],
        point[1] - projectedPoint[1],
      ];

      const magnitude = Math.sqrt(
        directionVector[0] ** 2 + directionVector[1] ** 2
      );
      const normalizedDirection: Point2 = [
        directionVector[0] / magnitude,
        directionVector[1] / magnitude,
      ];

      const extensionMultiplier = 10000;

      const extendedPoint: Point2 = [
        projectedPoint[0] + normalizedDirection[0] * extensionMultiplier,
        projectedPoint[1] + normalizedDirection[1] * extensionMultiplier,
      ];

      let intersectionPoint: Point2 | null = null;

      const polygonEdges =
        closestShape.sheetShapePolygon?.multipolygon.polygons.flatMap(
          (polygon) => {
            const edges = polygon.exterior.points.map(
              (point, index) =>
                [
                  toPoint2(point),
                  toPoint2(
                    polygon.exterior.points[
                      (index + 1) % polygon.exterior.points.length
                    ]
                  ),
                ] as LineSegment2
            );
            return edges;
          }
        ) || [];

      for (const edge of polygonEdges) {
        if (!areSegmentsEqual(edge, closestEdge, 0.01)) {
          const potentialIntersection = getSegmentIntersection(
            [projectedPoint, extendedPoint],
            edge
          );
          if (isPoint2(potentialIntersection)) {
            intersectionPoint = potentialIntersection as Point2;
            break;
          }
        }
      }

      if (intersectionPoint) {
        const edge = [projectedPoint, intersectionPoint] as LineSegment2;
        return { segment: edge, shape: closestShape };
      }
    }

    return { segment: null, shape: null };
  }

  private _getClosestEdge(
    point: Point2,
    edges: LineSegment2[]
  ): LineSegment2 | null {
    let closestEdge: LineSegment2 | null = null;
    let minDistance = Infinity;

    for (const edge of edges) {
      const distance = getDistanceToSegment(point, edge);
      if (distance < minDistance) {
        minDistance = distance;
        closestEdge = edge;
      }
    }

    return closestEdge;
  }

  // eslint-disable-next-line complexity
  private _getDirectedSegmentFromPointInShape(
    point: Point2,
    direction: Point2,
    shape: SheetShapeDeepFragment
  ): LineSegment2 | null {
    // Normalize the direction vector
    const magnitude = Math.sqrt(direction[0] ** 2 + direction[1] ** 2);
    const normalizedDirection: Point2 = [
      direction[0] / magnitude,
      direction[1] / magnitude,
    ];

    const extensionMultiplier = 1000;

    // Create extended points in both directions
    const extendedPoint1: Point2 = [
      point[0] + normalizedDirection[0] * extensionMultiplier,
      point[1] + normalizedDirection[1] * extensionMultiplier,
    ];

    const extendedPoint2: Point2 = [
      point[0] - normalizedDirection[0] * extensionMultiplier,
      point[1] - normalizedDirection[1] * extensionMultiplier,
    ];

    // Get all edges from the shape
    const polygonEdges =
      shape.sheetShapePolygon?.multipolygon.polygons.flatMap((polygon) => {
        const edges = polygon.exterior.points.map(
          (point, index) =>
            [
              toPoint2(point),
              toPoint2(
                polygon.exterior.points[
                  (index + 1) % polygon.exterior.points.length
                ]
              ),
            ] as LineSegment2
        );
        return edges;
      }) || [];

    let intersectionPoint1: Point2 | null = null;
    let intersectionPoint2: Point2 | null = null;
    let minDist1 = Infinity;
    let minDist2 = Infinity;
    const tolerance = 0.01;

    // Check each edge for intersections
    for (const edge of polygonEdges) {
      // Check intersection in first direction
      const potentialIntersection1 = getSegmentIntersection(
        [point, extendedPoint1],
        edge,
        tolerance
      );

      if (
        isPoint2(potentialIntersection1) &&
        !pointsAreEqual(point, potentialIntersection1, tolerance)
      ) {
        const dist = Math.hypot(
          potentialIntersection1[0] - point[0],
          potentialIntersection1[1] - point[1]
        );
        if (dist < minDist1) {
          intersectionPoint1 = potentialIntersection1 as Point2;
          minDist1 = dist;
        }
      }

      // Check intersection in opposite direction
      const potentialIntersection2 = getSegmentIntersection(
        [point, extendedPoint2],
        edge,
        tolerance
      );

      if (
        isPoint2(potentialIntersection2) &&
        !pointsAreEqual(point, potentialIntersection2, tolerance)
      ) {
        const dist = Math.hypot(
          potentialIntersection2[0] - point[0],
          potentialIntersection2[1] - point[1]
        );
        if (dist < minDist2) {
          intersectionPoint2 = potentialIntersection2 as Point2;
          minDist2 = dist;
        }
      }
    }

    // Return the complete segment if we found intersections in both directions
    if (intersectionPoint1 && intersectionPoint2) {
      return [intersectionPoint1, intersectionPoint2];
    }

    return null;
  }

  private _getBaseLineFromSplitSegment(
    splitSegment: LineSegment2,
    shape: SheetShapeDeepFragment,
    cursorPoint: Point2
  ): LineSegment2 | null {
    // Get splitSegment direction
    const splitDirection: Point2 = [
      splitSegment[1][0] - splitSegment[0][0],
      splitSegment[1][1] - splitSegment[0][1],
    ];

    // Normalize split direction
    const splitLength = Math.sqrt(
      splitDirection[0] ** 2 + splitDirection[1] ** 2
    );
    const splitNormal: Point2 = [
      splitDirection[0] / splitLength,
      splitDirection[1] / splitLength,
    ];

    // Get perpendicular direction (rotate 90 degrees)
    const perpDirection: Point2 = [-splitNormal[1], splitNormal[0]];

    // Collect all points from the shape
    const allPoints: Point2[] = [];
    shape.sheetShapePolygon?.multipolygon.polygons.forEach((polygon) => {
      polygon.exterior.points.forEach((point) => {
        allPoints.push([point.x, point.y]);
      });
    });

    // Project points onto both directions
    const projections = allPoints.map((point) => {
      // Vector from split segment start to point
      const toPoint: Point2 = [
        point[0] - splitSegment[0][0],
        point[1] - splitSegment[0][1],
      ];

      // Project onto perpendicular direction
      const perpProjection =
        toPoint[0] * perpDirection[0] + toPoint[1] * perpDirection[1];

      // Project onto split direction
      const splitProjection =
        toPoint[0] * splitNormal[0] + toPoint[1] * splitNormal[1];

      return {
        perpDistance: perpProjection,
        splitDistance: splitProjection,
        point: point,
      };
    });

    // Find min and max perpendicular projections
    const minPerpProj = projections.reduce(
      (min, curr) => (curr.perpDistance < min.perpDistance ? curr : min),
      projections[0]
    );
    const maxPerpProj = projections.reduce(
      (max, curr) => (curr.perpDistance > max.perpDistance ? curr : max),
      projections[0]
    );

    // Find min and max split direction projections
    const minSplitProj = projections.reduce(
      (min, curr) => (curr.splitDistance < min.splitDistance ? curr : min),
      projections[0]
    );
    const maxSplitProj = projections.reduce(
      (max, curr) => (curr.splitDistance > max.splitDistance ? curr : max),
      projections[0]
    );

    if (!minPerpProj || !maxPerpProj || !minSplitProj || !maxSplitProj)
      return null;

    // Project cursor onto split direction to determine which extreme is closer
    const cursorVector: Point2 = [
      cursorPoint[0] - splitSegment[0][0],
      cursorPoint[1] - splitSegment[0][1],
    ];
    const cursorProjection =
      cursorVector[0] * splitNormal[0] + cursorVector[1] * splitNormal[1];

    // Choose the extreme point closer to cursor projection
    const basePosition =
      Math.abs(cursorProjection - minSplitProj.splitDistance) <
      Math.abs(cursorProjection - maxSplitProj.splitDistance)
        ? minSplitProj
        : maxSplitProj;

    // Create baseline segment at the chosen position
    const baseLine: LineSegment2 = [
      [
        splitSegment[0][0] +
          splitNormal[0] * basePosition.splitDistance +
          perpDirection[0] * maxPerpProj.perpDistance,
        splitSegment[0][1] +
          splitNormal[1] * basePosition.splitDistance +
          perpDirection[1] * maxPerpProj.perpDistance,
      ],
      [
        splitSegment[0][0] +
          splitNormal[0] * basePosition.splitDistance +
          perpDirection[0] * minPerpProj.perpDistance,
        splitSegment[0][1] +
          splitNormal[1] * basePosition.splitDistance +
          perpDirection[1] * minPerpProj.perpDistance,
      ],
    ];

    return baseLine;
  }

  private _createSnappedSegment(
    segment: LineSegment2,
    baseLine: LineSegment2
  ): LineSegment2 {
    // Get the start and end points of the segment
    const [startPoint, endPoint] = segment;

    // Get unit scale from viewer model
    const unitScale = this.viewer.model?.getUnitScale() || 1;

    // Calculate baseline length in mm
    const baselineVector: Point2 = [
      baseLine[1][0] - baseLine[0][0],
      baseLine[1][1] - baseLine[0][1],
    ];
    const baselineLength =
      Math.sqrt(
        baselineVector[0] * baselineVector[0] +
          baselineVector[1] * baselineVector[1]
      ) * unitScale;

    // Convert absolute threshold to relative threshold for this baseline
    const relativeThreshold =
      baselineLength > 0 ? ABSOLUTE_PROXIMITY_THRESHOLD / baselineLength : 0.1; // Fallback to 10% if length is 0

    // Calculate relative positions of segment points when projected onto baseline
    const startPosOnBaseline = calculateRelativePosition(startPoint, baseLine);
    const endPosOnBaseline = calculateRelativePosition(endPoint, baseLine);

    // Normalize baseline direction
    const baselineDirection: Point2 = [
      baselineVector[0] / (baselineLength / unitScale),
      baselineVector[1] / (baselineLength / unitScale),
    ];

    // Get perpendicular direction (rotate 90 degrees)
    const perpendicularDirection: Point2 = [
      -baselineDirection[1],
      baselineDirection[0],
    ];

    // Function to calculate perpendicular distance from point to baseline
    const getPerpendicularDistance = (point: Point2): number => {
      // Vector from baseline start to point
      const vectorToPoint: Point2 = [
        point[0] - baseLine[0][0],
        point[1] - baseLine[0][1],
      ];

      // Dot product with perpendicular direction gives distance
      return (
        vectorToPoint[0] * perpendicularDirection[0] +
        vectorToPoint[1] * perpendicularDirection[1]
      );
    };

    // Calculate perpendicular distances
    const startPerpendicularDistance = getPerpendicularDistance(startPoint);
    const endPerpendicularDistance = getPerpendicularDistance(endPoint);

    // Create new start point
    let newStartPoint: Point2;
    if (startPosOnBaseline < relativeThreshold) {
      // Snap to be perpendicular to baseline start
      newStartPoint = [
        baseLine[0][0] + startPerpendicularDistance * perpendicularDirection[0],
        baseLine[0][1] + startPerpendicularDistance * perpendicularDirection[1],
      ];
    } else if (startPosOnBaseline > 1 - relativeThreshold) {
      // Snap to be perpendicular to baseline end
      newStartPoint = [
        baseLine[1][0] + startPerpendicularDistance * perpendicularDirection[0],
        baseLine[1][1] + startPerpendicularDistance * perpendicularDirection[1],
      ];
    } else {
      // Keep original
      newStartPoint = [...startPoint];
    }

    // Create new end point
    let newEndPoint: Point2;
    if (endPosOnBaseline < relativeThreshold) {
      // Snap to be perpendicular to baseline start
      newEndPoint = [
        baseLine[0][0] + endPerpendicularDistance * perpendicularDirection[0],
        baseLine[0][1] + endPerpendicularDistance * perpendicularDirection[1],
      ];
    } else if (endPosOnBaseline > 1 - relativeThreshold) {
      // Snap to be perpendicular to baseline end
      newEndPoint = [
        baseLine[1][0] + endPerpendicularDistance * perpendicularDirection[0],
        baseLine[1][1] + endPerpendicularDistance * perpendicularDirection[1],
      ];
    } else {
      // Keep original
      newEndPoint = [...endPoint];
    }

    return [newStartPoint, newEndPoint];
  }

  private _drawIntermediateEdge(color?: string): void {
    // Clear any existing preview
    this._clearIntermediateEdge();

    if (!this._state.intermediateEdge) return;

    // Convert API coordinates to viewer coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    const startPoint = converter.convertToAutodeskPoint({
      x: this._state.intermediateEdge[0][0],
      y: this._state.intermediateEdge[0][1],
    });
    const endPoint = converter.convertToAutodeskPoint({
      x: this._state.intermediateEdge[1][0],
      y: this._state.intermediateEdge[1][1],
    });

    // Create a temporary line shape for preview
    this._previewShape = new Autodesk.Edit2D.Polyline([startPoint, endPoint]);

    // Set dashed style for preview (orange default, overridable)
    this._previewShape.style.lineColor = color || ChakraColor.orange300;
    this._previewShape.style.lineWidth = 2;

    // Add to the layer
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      this.edit2d.defaultContext.layer.addShape(this._previewShape);
      this.edit2d.defaultContext.layer.update();
    }
  }

  private _clearIntermediateEdge(): void {
    // Clear any intermediate edge visualization
    if (
      this._previewShape &&
      this.edit2d.defaultContext &&
      this.edit2d.defaultContext.layer
    ) {
      this.edit2d.defaultContext.layer.removeShape(this._previewShape);
      this.edit2d.defaultContext.layer.update();
      this._previewShape = null;
    }
  }

  private _renderStaticShapes(): void {
    // Clear existing static shapes
    this._clearStaticShapes();

    // Convert API coordinates to viewer coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    // Create shapes for each drain line
    for (const drainLine of this._state.drainLines) {
      const startPoint = converter.convertToAutodeskPoint({
        x: drainLine[0][0],
        y: drainLine[0][1],
      });
      const endPoint = converter.convertToAutodeskPoint({
        x: drainLine[1][0],
        y: drainLine[1][1],
      });

      const lineSegment = new Autodesk.Edit2D.Polyline([startPoint, endPoint]);

      // Set green style for static shapes
      lineSegment.style.lineColor = ChakraColor.green400;
      lineSegment.style.lineWidth = 4;

      this._staticShapes.push(lineSegment);

      // Add to the layer
      if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
        this.edit2d.defaultContext.layer.addShape(lineSegment);
      }
    }

    // Update the layer
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      this.edit2d.defaultContext.layer.update();
    }
  }

  private _clearStaticShapes(): void {
    // Clear all static shapes
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      for (const shape of this._staticShapes) {
        this.edit2d.defaultContext.layer.removeShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }
    this._staticShapes = [];
  }

  private _handleStaticLineClick(cursorPosition: THREE.Vector3): boolean {
    // Check if we clicked on any static drain line
    for (let i = 0; i < this._staticShapes.length; i++) {
      const shape = this._staticShapes[i];

      // Use hitTest to check if the cursor position intersects with the shape
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (
        (shape as any).hitTest &&
        (shape as any).hitTest(cursorPosition.x, cursorPosition.y, 0.1)
      ) {
        // Remove the clicked drain line
        this._state.drainLines.splice(i, 1);

        // Re-render all static shapes
        this._renderStaticShapes();

        // Update the result callback
        this._updateShapes();

        return true; // We handled the click
      }
    }

    return false; // No static line was clicked
  }

  private _handleStaticLineHover(cursorPosition: THREE.Vector3): void {
    // Check if we're hovering over any static drain line
    let isHovering = false;

    for (const shape of this._staticShapes) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (
        (shape as any).hitTest &&
        (shape as any).hitTest(cursorPosition.x, cursorPosition.y, 0.1)
      ) {
        isHovering = true;
        break;
      }
    }

    // Update cursor based on hover state
    if (isHovering) {
      this.viewer.impl.canvas.style.cursor = 'pointer';
    } else {
      this.viewer.impl.canvas.style.cursor = 'crosshair';
    }
  }

  private _computeCrossSection(): SlopedInsulationCrossSection | null {
    if (
      !this._state.drainLines ||
      this._state.drainLines.length === 0 ||
      !this._state.baseLine ||
      !this._state.activeShape
    ) {
      return null;
    }

    const lowPoints = this._computeLowPoints();
    const processedLowPoints = this._processLowPointsForEdgeCases(lowPoints);

    return {
      baseLine: this._state.baseLine,
      slope: this._state.crossSection?.slope || SLOPE,
      lowPoints: processedLowPoints,
      minHeight: this._state.crossSection?.minHeight || this._minHeight,
    };
  }

  private _computeLowPoints(): number[] {
    const baselineVec = [
      this._state.baseLine![1][0] - this._state.baseLine![0][0],
      this._state.baseLine![1][1] - this._state.baseLine![0][1],
    ];
    const baselineLength = Math.sqrt(
      baselineVec[0] * baselineVec[0] + baselineVec[1] * baselineVec[1]
    );
    const relativeThreshold =
      baselineLength > 0 ? ABSOLUTE_PROXIMITY_THRESHOLD / baselineLength : 0.1;

    return this._state.drainLines.map((drainLine) => {
      const relPos = calculateRelativePosition(
        drainLine[0],
        this._state.baseLine!
      );
      // Check if relPos is close to 0 or 1 and snap it
      if (relPos < relativeThreshold) {
        return 0;
      } else if (relPos > 1 - relativeThreshold) {
        return 1;
      }
      return relPos;
    });
  }

  private _processLowPointsForEdgeCases(lowPoints: number[]): number[] {
    const baselineVec = [
      this._state.baseLine![1][0] - this._state.baseLine![0][0],
      this._state.baseLine![1][1] - this._state.baseLine![0][1],
    ];
    const baselineLength = Math.sqrt(
      baselineVec[0] * baselineVec[0] + baselineVec[1] * baselineVec[1]
    );
    const relativeThreshold =
      baselineLength > 0 ? ABSOLUTE_PROXIMITY_THRESHOLD / baselineLength : 0.1;

    // Remove duplicates and sort, but don't automatically add opposite end points
    // This prevents unwanted low points at the opposite end of the baseline
    const uniqueLowPoints = [...new Set(lowPoints)].sort((a, b) => a - b);

    // Only ensure we have at least one low point if we have no drain lines
    // This prevents the cross section from breaking when there are no drain lines
    if (uniqueLowPoints.length === 0) {
      return [0.5]; // Default to middle if no drain lines
    }

    return uniqueLowPoints;
  }

  private _renderCrossSectionPreview(): void {
    // Clear existing actual cross section shapes
    this._clearActualCrossSection();

    const crossSection = this._computeCrossSection();
    if (!crossSection) return;

    try {
      // Get unit scale from viewer model
      const unitScale = this.viewer.model?.getUnitScale() || 1;

      // Compute trapezoids with proper unit scale
      // Convert minHeight from meters to millimeters for computation
      const { trapezoids: crossSectionTrapezoids, maxHeight: maxHeightVC } =
        computeTrapezoids(
          crossSection.baseLine,
          crossSection.lowPoints,
          crossSection.minHeight * 1000 * VERTICAL_MULTIPLIER * unitScale, // Convert meters to mm
          SLOPE * VERTICAL_MULTIPLIER,
          unitScale
        );

      // Compute offset direction from the last drain line
      const lastDrainLine =
        this._state.drainLines[this._state.drainLines.length - 1];
      const offsettedTrapezoids = this._computeOffsetTrapezoids(
        crossSectionTrapezoids,
        maxHeightVC,
        lastDrainLine
      );

      this._renderTrapezoidsAsShapes(offsettedTrapezoids, false);
    } catch (error) {
      console.warn('Error rendering cross section preview:', error);
    }
  }

  private _clearCrossSectionPreview(): void {
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      for (const shape of this._crossSectionPreviewShapes) {
        this.edit2d.defaultContext.layer.removeShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }
    this._crossSectionPreviewShapes = [];
  }

  private _renderIntermediateCrossSectionPreview(
    point: Point2,
    baseLine: LineSegment2,
    splitSegment: LineSegment2
  ): void {
    // Clear existing preview shapes
    this._clearCrossSectionPreview();

    try {
      const lowPoints = this._computeIntermediateLowPoints(
        baseLine,
        splitSegment
      );
      const unitScale = this.viewer.model?.getUnitScale() || 1;

      // Compute trapezoids with proper unit scale
      const { trapezoids: crossSectionTrapezoids, maxHeight: maxHeightVC } =
        computeTrapezoids(
          baseLine,
          lowPoints,
          this._minHeight * 1000 * VERTICAL_MULTIPLIER * unitScale, // Use stored minHeight (convert meters to mm)
          SLOPE * VERTICAL_MULTIPLIER,
          unitScale
        );

      const offsettedTrapezoids = this._computeOffsetTrapezoids(
        crossSectionTrapezoids,
        maxHeightVC,
        splitSegment
      );

      this._renderTrapezoidsAsShapes(offsettedTrapezoids, true);
    } catch (error) {
      console.warn(
        'Error rendering intermediate cross section preview:',
        error
      );
    }
  }

  private _computeIntermediateLowPoints(
    baseLine: LineSegment2,
    splitSegment: LineSegment2
  ): number[] {
    const baselineVec = [
      baseLine[1][0] - baseLine[0][0],
      baseLine[1][1] - baseLine[0][1],
    ];
    const baselineLength = Math.sqrt(
      baselineVec[0] * baselineVec[0] + baselineVec[1] * baselineVec[1]
    );
    const relativeThreshold =
      baselineLength > 0 ? ABSOLUTE_PROXIMITY_THRESHOLD / baselineLength : 0.1;

    // Create a temporary lowPoints array with the snapped segment's start point
    const snappedPointRelPos = calculateRelativePosition(
      splitSegment[0],
      baseLine
    );
    let lowPoints: number[];

    // Check if snappedPointRelPos is close to 0 or 1 and snap it
    if (snappedPointRelPos < relativeThreshold) {
      lowPoints = [0];
    } else if (snappedPointRelPos > 1 - relativeThreshold) {
      lowPoints = [1];
    } else {
      lowPoints = [snappedPointRelPos];
    }

    // Don't automatically add opposite end points for preview
    // This prevents unwanted low points in the preview cross section
    return lowPoints;
  }

  private _computeOffsetTrapezoids(
    trapezoids: Trapezoid2[],
    maxHeight: number,
    directionSegment: LineSegment2
  ): Trapezoid2[] {
    const offsetDirection = [
      directionSegment[1][0] - directionSegment[0][0],
      directionSegment[1][1] - directionSegment[0][1],
    ];
    const offsetDirectionMagnitude = Math.sqrt(
      offsetDirection[0] ** 2 + offsetDirection[1] ** 2
    );
    const offsetDirectionNormalized: Point2 = [
      offsetDirection[0] / offsetDirectionMagnitude,
      offsetDirection[1] / offsetDirectionMagnitude,
    ];

    return trapezoids.map(
      (trapezoid) =>
        trapezoid.map((point) => [
          point[0] -
            offsetDirectionNormalized[0] * (OFFSET_FROM_ROOF + maxHeight),
          point[1] -
            offsetDirectionNormalized[1] * (OFFSET_FROM_ROOF + maxHeight),
        ]) as Trapezoid2
    );
  }

  private _renderTrapezoidsAsShapes(
    trapezoids: Trapezoid2[],
    isPreview = false
  ): void {
    if (!this.edit2d.defaultContext || !this.edit2d.defaultContext.layer) {
      return;
    }

    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    const shapeArray = isPreview
      ? this._crossSectionPreviewShapes
      : this._actualCrossSectionShapes;
    const lineColor = isPreview ? ChakraColor.orange400 : ChakraColor.green400;
    const fillColor = isPreview ? ChakraColor.orange200 : ChakraColor.green200;

    for (const trapezoid of trapezoids) {
      const polygon = trapezoidToPolygon2(trapezoid);
      const convertedPoints = polygon.exterior.map((point) =>
        converter.convertToAutodeskPoint({ x: point[0], y: point[1] })
      );

      // Create SVG path for the polygon
      const svgPath =
        'M ' +
        convertedPoints.map((point) => `${point.x},${point.y}`).join(' L ') +
        ' Z';
      const svg = `<path d="${svgPath}"/>`;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

      shape.style.lineColor = lineColor;
      shape.style.fillColor = fillColor;
      shape.style.lineWidth = 1;
      shape.style.fillAlpha = 0.5;
      this.edit2d.defaultContext.layer.addShape(shape);
      shapeArray.push(shape);
    }
    this.edit2d.defaultContext.layer.update();
  }

  private _renderActualCrossSection(): void {
    // Clear existing actual cross section shapes
    this._clearActualCrossSection();

    if (!this._state.crossSection || !this._state.activeShape) return;

    try {
      // Get unit scale from viewer model
      const unitScale = this.viewer.model?.getUnitScale() || 1;

      // Calculate baseline length and relative threshold
      const baselineVec = [
        this._state.crossSection.baseLine[1][0] -
          this._state.crossSection.baseLine[0][0],
        this._state.crossSection.baseLine[1][1] -
          this._state.crossSection.baseLine[0][1],
      ];
      const baselineLength = Math.sqrt(
        baselineVec[0] * baselineVec[0] + baselineVec[1] * baselineVec[1]
      );
      const relativeThreshold =
        baselineLength > 0
          ? ABSOLUTE_PROXIMITY_THRESHOLD / baselineLength
          : 0.1;

      // Compute trapezoids with proper unit scale
      const { trapezoids: crossSectionTrapezoids, maxHeight: maxHeightVC } =
        computeTrapezoids(
          this._state.crossSection.baseLine,
          this._state.crossSection.lowPoints,
          this._state.crossSection.minHeight *
            1000 *
            VERTICAL_MULTIPLIER *
            unitScale, // Convert meters to mm for computation
          this._state.crossSection.slope * VERTICAL_MULTIPLIER,
          unitScale
        );

      // Calculate offset direction from the first drain line
      const firstDrainLine = this._state.drainLines[0];
      if (!firstDrainLine) return;

      const offsetDirection = [
        firstDrainLine[1][0] - firstDrainLine[0][0],
        firstDrainLine[1][1] - firstDrainLine[0][1],
      ];
      const offsetDirectionMagnitude = Math.sqrt(
        offsetDirection[0] ** 2 + offsetDirection[1] ** 2
      );
      const offsetDirectionNormalized: Point2 = [
        offsetDirection[0] / offsetDirectionMagnitude,
        offsetDirection[1] / offsetDirectionMagnitude,
      ];

      // Offset trapezoids
      const offsettedTrapezoids: Trapezoid2[] = crossSectionTrapezoids.map(
        (trapezoid) =>
          trapezoid.map((point) => [
            point[0] -
              offsetDirectionNormalized[0] * (OFFSET_FROM_ROOF + maxHeightVC),
            point[1] -
              offsetDirectionNormalized[1] * (OFFSET_FROM_ROOF + maxHeightVC),
          ]) as Trapezoid2
      );

      // Convert to Edit2D shapes with proper coordinate transformation
      if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
        const converter = new ApiCoordinatesConverter(
          this.viewer,
          this.activeSheetData.viewport
        );

        for (const trapezoid of offsettedTrapezoids) {
          const polygon = trapezoidToPolygon2(trapezoid);
          const convertedPoints = polygon.exterior.map((point) =>
            converter.convertToAutodeskPoint({ x: point[0], y: point[1] })
          );

          // Create SVG path for the polygon
          const svgPath =
            'M ' +
            convertedPoints
              .map((point) => `${point.x},${point.y}`)
              .join(' L ') +
            ' Z';
          const svg = `<path d="${svgPath}"/>`;

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

          shape.style.lineColor = ChakraColor.green400;
          shape.style.fillColor = ChakraColor.green200;
          shape.style.lineWidth = 1;
          shape.style.fillAlpha = 0.5;
          this.edit2d.defaultContext.layer.addShape(shape);
          this._actualCrossSectionShapes.push(shape);
        }
        this.edit2d.defaultContext.layer.update();
      }
    } catch (error) {
      console.warn('Error rendering actual cross section:', error);
    }
  }

  private _clearActualCrossSection(): void {
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      for (const shape of this._actualCrossSectionShapes) {
        this.edit2d.defaultContext.layer.removeShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }
    this._actualCrossSectionShapes = [];
  }

  private _updateShapes(): void {
    // Render the static shapes
    this._renderStaticShapes();

    // Render cross section preview
    this._renderCrossSectionPreview();

    // Convert drain lines to the format expected by the result callback
    const points = this._state.drainLines.flatMap((segment) => [
      { x: segment[0][0], y: segment[0][1] },
      { x: segment[1][0], y: segment[1][1] },
    ]);

    // Compute cross section for the result
    const crossSection = this._computeCrossSection();

    // Store drain line data for later use in the hook
    if (this._onResult) {
      // Convert API coordinates to viewer coordinates for the callback
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );

      // Convert drain lines to viewer coordinates format expected by updatePointsCallback
      const viewerPoints = this._state.drainLines.map((segment) => [
        converter.convertToAutodeskPoint({
          x: segment[0][0],
          y: segment[0][1],
        }),
        converter.convertToAutodeskPoint({
          x: segment[1][0],
          y: segment[1][1],
        }),
      ]);

      // Call the callback with the viewer coordinates format expected by updatePointsCallback
      this._onResult(viewerPoints);
    }
  }
}
